/**
 * Lightweight translation lookup for the guided tour on desktop. Desktop
 * doesn't use i18next — it has a custom `translations` object in
 * `src/lang/index.js`. To avoid bolting i18next on just for this feature
 * we ship our own tiny lookup that reads the selected language off
 * localStorage and falls back to Hebrew.
 *
 * Shared key shape with Admin-App tours:
 *   tours.welcome.title, tours.services.step1.body, ...
 */

const STRINGS = {
  he: {
    "tours.welcome.title": "היי! שמחים שאתם כאן",
    "tours.welcome.body":
      "במדריך הזה אראה לכם איך מנהלים את השירותים שלכם, איך מגדירים שעות קבלה ואיך קובעים תורים.",
    "tours.welcome.cta": "מתחילים",
    "tours.welcome.laterCta": "אחר כך",
    "tours.welcome.helpFromMenu": "תמיד ניתן להפעיל אותי שוב מתפריט \"עזרה ומדריכים\".",
    "tours.header.button": "מדריך לשלבים ראשונים",
    "tours.header.buttonCompleted": "כל המדריכים הושלמו",
    "tours.list.title": "עזרה ומדריכים",
    "tours.list.subtitle": "בחרו מדריך כדי לצאת לסיור",
    "tours.list.status_completed": "הושלם",
    "tours.list.status_pending": "טרם התחיל",
    "tours.list.status_inProgress": "בתהליך · שלב {{step}}",
    "tours.player.next": "הבא",
    "tours.player.previous": "קודם",
    "tours.player.close": "סיים מדריך",
    "tours.player.skip": "דלגו",
    "tours.services.title": "איך מגדירים שירותים",
    "tours.services.step1.body":
      "בואו נגדיר את השירותים שלכם — כמו תספורת, לק, עיסוי וכו'.",
    "tours.services.step2.body": "לחצו על \"השירותים שלי\" בתפריט הצידי.",
    "tours.services.step3.body": "לחצו על + כדי להוסיף שירות חדש.",
    "tours.services.step3.hint": "לחצו על האזור הורוד",
    "tours.services.step4.body": "מלאו את פרטי השירות, ובסיום לחצו על \"צור שירות חדש\".",
    "tours.workingHours.title": "הגדרת שעות קבלה",
    "tours.workingHours.step1.body": "עכשיו נגדיר מתי העסק פתוח — שעות הקבלה לפי יום.",
    "tours.workingHours.step2.body": "בחרו יום מהרשימה להגדרת שעות.",
    "tours.workingHours.step3.body": "קבעו שעת פתיחה וסגירה לאותו יום.",
    "tours.workingHours.step4.body": "שמרו את השינויים — זהו, שעות הקבלה מוגדרות!",
    "tours.calendar.title": "יומן וקביעת תורים",
    "tours.calendar.step1.body": "כך קובעים תור ללקוח חדש ביומן.",
    "tours.calendar.step2.body": "לחצו על + כדי לקבוע תור חדש.",
    "tours.calendar.step3.body": "בחרו לקוח קיים או הוסיפו חדש.",
    "tours.calendar.step4.body": "אשרו את התור ונוספנו ליומן.",
  },
  eng: {
    "tours.welcome.title": "Hey! Glad you're here",
    "tours.welcome.body":
      "In this guide I'll show you how to manage your services, set up working hours, and book appointments.",
    "tours.welcome.cta": "Let's go",
    "tours.welcome.laterCta": "Later",
    "tours.welcome.helpFromMenu": "You can always restart me from Help & Guides.",
    "tours.header.button": "First steps guide",
    "tours.header.buttonCompleted": "All guides complete",
    "tours.list.title": "Help & Guides",
    "tours.list.subtitle": "Pick a guide to start the tour",
    "tours.list.status_completed": "Completed",
    "tours.list.status_pending": "Not started",
    "tours.list.status_inProgress": "In progress · step {{step}}",
    "tours.player.next": "Next",
    "tours.player.previous": "Previous",
    "tours.player.close": "End tour",
    "tours.player.skip": "Skip",
    "tours.services.title": "How to set up services",
    "tours.services.step1.body":
      "Let's set up your services — haircut, polish, massage and so on.",
    "tours.services.step2.body": "Click \"My Services\" in the side nav.",
    "tours.services.step3.body": "Click + to add a new service.",
    "tours.services.step3.hint": "Click the pink area",
    "tours.services.step4.body": "Fill in the service details, then click \"Create new service\" to finish.",
    "tours.workingHours.title": "Set working hours",
    "tours.workingHours.step1.body":
      "Now let's set when your business is open — hours per day.",
    "tours.workingHours.step2.body": "Pick a day from the list to set hours.",
    "tours.workingHours.step3.body": "Choose opening and closing times.",
    "tours.workingHours.step4.body": "Save — your hours are configured!",
    "tours.calendar.title": "Calendar & bookings",
    "tours.calendar.step1.body": "Here's how to book an appointment for a client.",
    "tours.calendar.step2.body": "Click + to create a booking.",
    "tours.calendar.step3.body": "Pick an existing client or add a new one.",
    "tours.calendar.step4.body": "Confirm and the appointment is on the calendar.",
  },
};

const getLang = () => {
  // Matches the desktop's existing convention
  try {
    const raw = localStorage.getItem("language");
    if (raw === "eng") return "eng";
  } catch (e) {}
  return "he";
};

export const t = (key, vars = {}) => {
  const lang = getLang();
  const raw = (STRINGS[lang] && STRINGS[lang][key]) || STRINGS.he[key] || key;
  return raw.replace(/\{\{(\w+)\}\}/g, (_, name) => (vars[name] ?? ""));
};
